import { GripVertical, Trash2, Heading2, ChevronUp, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import type { HeadingBlock } from "@/types/helpArticle";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

interface DragHandleProps {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
}

interface MobileReorderProps {
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

interface HeadingBlockEditorProps {
  block: HeadingBlock;
  onChange: (block: HeadingBlock) => void;
  onDelete: () => void;
  dragHandleProps?: DragHandleProps;
  mobileReorderProps?: MobileReorderProps;
}

export function HeadingBlockEditor({
  block,
  onChange,
  onDelete,
  dragHandleProps,
  mobileReorderProps,
}: HeadingBlockEditorProps) {
  const handleContentChange = (content: string) => {
    onChange({ ...block, content });
  };

  return (
    <div className="group flex gap-2 sm:gap-3 p-3 sm:p-4 border rounded-lg bg-card hover:border-primary/50 transition-colors">
      {/* Drag Handle - hidden on mobile */}
      <div
        className="hidden sm:flex items-center cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
      >
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 space-y-2 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <Label className="flex items-center gap-1.5 sm:gap-2 text-sm font-medium text-muted-foreground">
            <Heading2 className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">Section Heading</span>
            <span className="sm:hidden">Heading</span>
            <Badge variant="secondary" className="text-xs font-mono">H2</Badge>
          </Label>
          <div className="flex items-center gap-1">
            {/* Mobile reorder buttons */}
            <div className="flex sm:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={mobileReorderProps?.onMoveUp}
                disabled={mobileReorderProps?.isFirst}
                className="h-9 w-9"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={mobileReorderProps?.onMoveDown}
                disabled={mobileReorderProps?.isLast}
                className="h-9 w-9"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="h-9 w-9 sm:h-8 sm:w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Input
          value={block.content}
          onChange={(e) => handleContentChange(e.target.value)}
          placeholder="Enter heading text..."
          className="text-base sm:text-lg font-semibold"
        />
      </div>
    </div>
  );
}
